import { useEffect, useRef, useState } from 'react';
import BasicModal from './BasicModal';
import Image from '../Image';
import { Mixpanel } from '../../services/Mixpanel';
import { disconnectCanva, getCanvaAccount } from '../../services/CanvaService';

// Lists the third-party accounts connected to Diffit (today only Canva) and lets the teacher
// disconnect them. Canva's app review requires showing the connection and a way to end it.
export default function ConnectedAccountsModal({ showModal, setShowModal, onDisconnected }) {
  const [account, setAccount] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Bumped by a disconnect, so a lookup still in flight cannot bring the connection back.
  const lookup = useRef(0);

  useEffect(() => {
    if (!showModal) return undefined;
    const id = ++lookup.current;
    const active = () => id === lookup.current;
    setAccount(null);
    setConfirming(false);
    setError('');
    getCanvaAccount()
      .then((result) => {
        if (!active()) return;
        setAccount(result);
        // Canva had already ended it (disconnected on Canva's side): drop the menu item too.
        if (!result.connected) onDisconnected();
      })
      // The name is a nicety: if it cannot be read, the connection is still listed.
      .catch(() => active() && setAccount({ connected: true, display_name: null }));
    return () => {
      lookup.current += 1;
    };
  }, [showModal]);

  const disconnect = async () => {
    setBusy(true);
    setError('');
    try {
      await disconnectCanva();
      lookup.current += 1;
      Mixpanel.track('Canva Disconnected');
      setAccount({ connected: false, display_name: null });
      onDisconnected();
    } catch {
      setError('Diffit could not disconnect Canva. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <BasicModal title='Connected Accounts' showModal={showModal} setShowModal={setShowModal}>
      <div className='mt-4'>
        {account && !account.connected ? (
          <p className='text-sm text-gray-500'>You have no connected accounts.</p>
        ) : (
          <div className='rounded-lg border border-gray-200 p-4'>
            <div className='flex items-center gap-3'>
              <Image src='/canva-logo.png' alt='' className='h-8 w-8 flex-shrink-0' />
              <div className='min-w-0 flex-1'>
                <p className='text-sm font-medium text-gray-900'>Canva</p>
                {/* The row renders at once; only the name line fills in, at the same height. */}
                {account ? (
                  <p className='truncate text-xs text-gray-500'>
                    {account.display_name ? `Connected as ${account.display_name}` : 'Connected'}
                  </p>
                ) : (
                  <p className='text-xs' role='status' aria-label='Loading Canva account'>
                    <span className='inline-block h-3 w-32 animate-pulse rounded bg-gray-200 align-middle' />
                  </p>
                )}
              </div>
              {!confirming && (
                <button
                  type='button'
                  onClick={() => setConfirming(true)}
                  className='text-sm font-medium text-gray-600 hover:text-gray-900'>
                  Disconnect
                </button>
              )}
            </div>
            {confirming && (
              <div className='mt-3 border-t border-gray-100 pt-3'>
                <p className='text-sm text-gray-700'>
                  Disconnect Canva? You'll sign in to Canva again the next time you export there.
                </p>
                <div className='mt-3 flex justify-end gap-2'>
                  <button
                    type='button'
                    disabled={busy}
                    onClick={() => setConfirming(false)}
                    className='rounded-md px-3 py-1.5 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'>
                    Cancel
                  </button>
                  <button
                    type='button'
                    disabled={busy}
                    onClick={disconnect}
                    className='rounded-md bg-red-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-red-500 disabled:opacity-60'>
                    {busy ? 'Disconnecting…' : 'Disconnect'}
                  </button>
                </div>
              </div>
            )}
            {error && (
              <p className='mt-2 text-sm text-red-600' role='alert'>
                {error}
              </p>
            )}
          </div>
        )}
      </div>
    </BasicModal>
  );
}
